// show campaign by id => consumer endpoint allows non-authenticated access to all non-archived campaigns
import { useQuery } from '@tanstack/react-query';

import { createQueryOptions } from '../../../query';
import { useStoreApi } from '../../api';
import { CampaignSearch } from '../../campaigns/types';
import { storeItemWithCrowdfundingType } from '../../campaigns/utils/storeItemTypes';
import { storeKeys } from '../../query-key-factory';
import { StoreItemResponse } from '../../types';
import { CampaignStoreItemTypes, DynamicCampaignStoreItemOrAthon } from '../../types/utils';

function useShowConsumerCampaignById<
  T extends CampaignStoreItemTypes,
  U = DynamicCampaignStoreItemOrAthon<T>,
>({
  campaignId,
  causeId,
  select,
  queryOptions,
}: {
  campaignId?: string;
  causeId?: string;
  select?: (data: CampaignStoreItemTypes) => U;
  queryOptions?: CampaignSearch;
}) {
  const StoreApi = useStoreApi();

  const queryParams = new URLSearchParams();
  if (causeId) {
    queryParams.set('cause_id', causeId);
  }

  const queryString = queryParams.toString();
  const { queryString: querySearchString } = createQueryOptions<CampaignSearch>({
    search: queryOptions,
    existingSearchString: queryString,
  });

  const getCampaign = async () => {
    const { data } = await StoreApi.get<StoreItemResponse<CampaignStoreItemTypes>>(
      // use consumer endpoint to allow non-authenticated access to all non-archived campaigns
      `/consumer/store_items/${campaignId}?${querySearchString}`
    );
    return storeItemWithCrowdfundingType(data.store_item);
  };

  // Create a read-only store key
  const storeKey =
    causeId && campaignId ? storeKeys.id(causeId).consumer.campaigns.id(campaignId).detail : [];
  // Create query key from the store key
  const queryKey = [...storeKey];

  if (querySearchString) queryKey.push(querySearchString);

  return useQuery({
    queryKey: queryKey,
    queryFn: getCampaign,
    enabled: !!campaignId && !!causeId,
    select,
  });
}

export { useShowConsumerCampaignById };
